/**
 * Navigation component - combines AppHeader and Breadcrumb navigation
 *
 * Provides complete navigation solution for all applications including
 * top-level app navigation and hierarchical breadcrumb navigation.
 *
 * Requirements: 1.6, 2.3
 */

import React from 'react';
import { AppHeader, type CurrentApp } from '../AppHeader';
import { Breadcrumb, generateBreadcrumbItems } from '../Breadcrumb';
import styles from './Navigation.module.css';

export interface NavigationProps {
  /** Current application for nav highlighting */
  currentApp: CurrentApp;
  /** Current page within the app (optional) */
  currentPage?: string;
  /** Whether to show breadcrumb navigation */
  showBreadcrumb?: boolean;
}

/**
 * Complete navigation component
 *
 * Features:
 * - Top-level application navigation via AppHeader (Req 1.6)
 * - Hierarchical breadcrumb navigation (Req 2.3)
 * - Consistent styling across all applications
 * - Responsive design
 * - Accessibility support
 */
export const Navigation: React.FC<NavigationProps> = ({
  currentApp,
  currentPage,
  showBreadcrumb = true,
}) => {
  const breadcrumbItems = generateBreadcrumbItems(currentApp, currentPage);

  return (
    <div className={styles.navigation}>
      <AppHeader currentApp={currentApp} />
      {showBreadcrumb && breadcrumbItems.length > 1 && (
        <div className={styles.breadcrumbContainer}>
          <div className={styles.breadcrumbWrapper}>
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>
      )}
    </div>
  );
};
